import {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { deviceDetect } from 'react-device-detect';
import {
  ProductSessionProvider,
  useProductSession,
} from '@xinosolutions/xs-auth-react';
import { createLearnixProductApiConfig } from '../config/learnixProductApi';

const LEARNIX_MEMBER_INIT_BODY = { device_info: deviceDetect() };

const LearnixProductContext = createContext(undefined);

function LearnixProductInner({ children }) {
  const {
    workspace,
    loading,
    ready,
    needsOnboarding,
    failureReason,
    refresh,
    clear,
  } = useProductSession();

  const refreshLearnixSession = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      workspace,
      loading,
      ready,
      needsOnboarding,
      failureReason,
      refreshLearnixSession,
      clearLearnixSession: clear,
    }),
    [
      workspace,
      loading,
      ready,
      needsOnboarding,
      failureReason,
      refreshLearnixSession,
      clear,
    ],
  );

  return (
    <LearnixProductContext.Provider value={value}>{children}</LearnixProductContext.Provider>
  );
}

export function LearnixProductProvider({ children }) {
  const productApiConfig = useMemo(() => createLearnixProductApiConfig(), []);

  return (
    <ProductSessionProvider
      config={productApiConfig}
      memberInitBody={LEARNIX_MEMBER_INIT_BODY}
    >
      <LearnixProductInner>{children}</LearnixProductInner>
    </ProductSessionProvider>
  );
}

export function useLearnixProduct() {
  const ctx = useContext(LearnixProductContext);
  if (!ctx) {
    throw new Error('useLearnixProduct must be used within LearnixProductProvider');
  }
  return ctx;
}
